// client/src/components/AdminLessons.js
// Lessons management component for superadmins

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { HeadlineLarge, BodyMedium, LabelMedium } from './Typography';

const AdminLessons = ({ onNavigateToLesson }) => {
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state with session persistence
  const [searchTerm, setSearchTerm] = useState(() => 
    sessionStorage.getItem('adminLessons_searchTerm') || ''
  );
  const [selectedCourse, setSelectedCourse] = useState(() => 
    sessionStorage.getItem('adminLessons_selectedCourse') || ''
  );
  const [selectedLevel, setSelectedLevel] = useState(() => 
    sessionStorage.getItem('adminLessons_selectedLevel') || ''
  );
  const [selectedLessonType, setSelectedLessonType] = useState(() => 
    sessionStorage.getItem('adminLessons_selectedLessonType') || ''
  );

  // Save filter state to session storage
  useEffect(() => {
    sessionStorage.setItem('adminLessons_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    sessionStorage.setItem('adminLessons_selectedCourse', selectedCourse);
  }, [selectedCourse]);

  useEffect(() => {
    sessionStorage.setItem('adminLessons_selectedLevel', selectedLevel);
  }, [selectedLevel]);

  useEffect(() => {
    sessionStorage.setItem('adminLessons_selectedLessonType', selectedLessonType);
  }, [selectedLessonType]);

  // Load courses for dropdown
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const coursesRef = collection(db, 'courses');
        const coursesSnapshot = await getDocs(coursesRef);
        const coursesData = [];
        coursesSnapshot.forEach(doc => {
          coursesData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        coursesData.sort((a, b) => (a.course_name || a.title || '').localeCompare(b.course_name || b.title || ''));
        setCourses(coursesData);
      } catch (error) {
        console.error('Error loading courses:', error);
        setError('Failed to load courses');
      }
    };

    loadCourses();
  }, []);

  // Load all lessons and courses
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load lessons
        const lessonsRef = collection(db, 'lessons');
        const lessonsQuery = query(lessonsRef, orderBy('level', 'asc'), orderBy('lesson_number', 'asc'));
        const lessonsSnapshot = await getDocs(lessonsQuery);
        
        const lessonsData = [];
        lessonsSnapshot.forEach(doc => {
          lessonsData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort by level, then by lesson number
        lessonsData.sort((a, b) => {
          if (a.level !== b.level) {
            return a.level - b.level;
          }
          return (a.lesson_number || 0) - (b.lesson_number || 0);
        });
        
        setLessons(lessonsData);
      } catch (error) {
        console.error('Error loading lessons data:', error);
        setError('Failed to load lessons. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter lessons based on search and filter criteria
  const filteredLessons = useMemo(() => {
    return lessons.filter(lesson => {
      // Search term filter (lesson title, headline, or description)
      const searchMatch = !searchTerm || 
        (lesson.title && lesson.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lesson.headline && lesson.headline.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lesson.description && lesson.description.toLowerCase().includes(searchTerm.toLowerCase()));

      // Course filter
      const courseMatch = !selectedCourse || lesson.course_id === selectedCourse;

      // Level filter
      const levelMatch = !selectedLevel || lesson.level === parseInt(selectedLevel);

      // Lesson type filter
      const typeMatch = !selectedLessonType || lesson.lesson_type === selectedLessonType;

      return searchMatch && courseMatch && levelMatch && typeMatch;
    });
  }, [lessons, searchTerm, selectedCourse, selectedLevel, selectedLessonType]);

  const handleAddLesson = () => {
    // Navigate to blank lesson edit page for new lesson
    onNavigateToLesson(null);
  };

  const handleEditLesson = (lessonId) => {
    // Navigate to lesson edit page for existing lesson
    onNavigateToLesson(lessonId);
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.course_name || course.title : 'Unknown Course';
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCourse('');
    setSelectedLevel('');
    setSelectedLessonType('');
    
    // Clear from session storage as well
    sessionStorage.removeItem('adminLessons_searchTerm');
    sessionStorage.removeItem('adminLessons_selectedCourse');
    sessionStorage.removeItem('adminLessons_selectedLevel');
    sessionStorage.removeItem('adminLessons_selectedLessonType');
  };

  const hasActiveFilters = searchTerm || selectedCourse || selectedLevel || selectedLessonType;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading lessons...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <HeadlineLarge color="gray-900">Lessons</HeadlineLarge>
        <button
          onClick={handleAddLesson}
          className="px-4 py-2 bg-blue-100 text-blue-700 border border-blue-300 rounded hover:bg-blue-200 transition-colors"
        >
          Add Lesson
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search Input */}
          <div>
            <LabelMedium color="gray-700" className="block mb-2">Search Lessons</LabelMedium>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, headline, or description..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Course Filter */}
          <div>
            <LabelMedium color="gray-700" className="block mb-2">Filter by Course</LabelMedium>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.course_name || course.title}
                </option>
              ))}
            </select>
          </div>

          {/* Level Filter */}
          <div>
            <LabelMedium color="gray-700" className="block mb-2">Filter by Level</LabelMedium>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Levels</option>
              {[1, 2, 3, 4, 5, 6].map(level => (
                <option key={level} value={level}>Level {level}</option>
              ))}
            </select>
          </div>

          {/* Lesson Type Filter */}
          <div>
            <LabelMedium color="gray-700" className="block mb-2">Filter by Type</LabelMedium>
            <select
              value={selectedLessonType}
              onChange={(e) => setSelectedLessonType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="text">Text</option>
              <option value="video">Video</option>
              <option value="graphic">Graphic</option>
              <option value="excerpt">Excerpt</option>
            </select>
          </div>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Results Summary */}
      {hasActiveFilters && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-blue-800">
              Showing {filteredLessons.length} of {lessons.length} lessons
              {searchTerm && ` matching "${searchTerm}"`}
              {selectedCourse && ` in ${getCourseName(selectedCourse)}`}
              {selectedLevel && ` at Level ${selectedLevel}`}
              {selectedLessonType && ` of type ${selectedLessonType}`}
            </div>
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* Lessons Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lesson Name
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Course
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Level
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredLessons.length > 0 ? (
              filteredLessons.map((lesson) => (
                <tr key={lesson.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <BodyMedium color="gray-900">{lesson.title || lesson.headline || 'Unnamed Lesson'}</BodyMedium>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <BodyMedium color="gray-600">{getCourseName(lesson.course_id)}</BodyMedium>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <BodyMedium color="gray-600">{lesson.level || '-'}</BodyMedium>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <BodyMedium color="gray-600">{lesson.lesson_number || '-'}</BodyMedium>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <BodyMedium color="gray-600">{lesson.lesson_type || '-'}</BodyMedium>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => handleEditLesson(lesson.id)}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center">
                  <div className="text-gray-500">
                    {hasActiveFilters ? (
                      <>
                        <p className="text-lg font-medium mb-2">No lessons match your filters</p>
                        <p className="text-sm mb-4">Try adjusting your search criteria or clearing the filters</p>
                        <button
                          onClick={clearFilters}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Clear All Filters
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-medium mb-2">No lessons found</p>
                        <p className="text-sm">Get started by creating your first lesson</p>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-500 text-center">
        Showing {filteredLessons.length} lesson{filteredLessons.length !== 1 ? 's' : ''}
        {hasActiveFilters && ` of ${lessons.length} total`}
      </div>
    </div>
  );
};

export default AdminLessons;
